<template>
  <f7-page class="nutrition-step-pao">
    <section id="nutrition-step-pao">
      <navbar
        :text="`${currentPlantation.company} > ${currentPlantation.explotation} > ${currentPlantation.parcel} > ${currentPlantation.sector} > ${currentPlantation.name} > ${$t('Utilities.nutrition')}`"
      />
      <PopupConfirmWizardExit />
      <stepper />

      <div class="content-devx sigpac-section">
        <div class="sigpac-header">
          <span class="sigpac-title">{{ $t('Utilities.pao.sigpacTitle') }}</span>
          <i
            id="sigpac-info-icon"
            class="dx-icon-info sigpac-info"
            @mouseenter="sigpacTooltipVisible = true"
            @mouseleave="sigpacTooltipVisible = false"
          />
          <DxPopover
            target="#sigpac-info-icon"
            :visible="sigpacTooltipVisible"
            :width="320"
            position="right"
            :hide-on-outside-click="false"
            :wrapper-attr="{ class: 'sigpac-tooltip-popover' }"
          >
            <div class="sigpac-tooltip-body">
              {{ $t('Utilities.pao.sigpacTooltip') }}
            </div>
          </DxPopover>
        </div>

        <div v-if="sigpacLoading" class="sigpac-loading">
          <DxLoadIndicator :height="24" :width="24" />
          <span class="sigpac-loading-text">{{ $t('Utilities.pao.sigpacLoading') }}</span>
        </div>

        <div v-else class="sigpac-rows">
          <div
            v-for="(code, idx) in plantationSigpac"
            :key="`sigpac-plant-${idx}`"
            class="sigpac-row"
          >
            <div class="sigpac-label">
              {{ `${$t('Utilities.pao.sigpac')} ${idx + 1}` }}
            </div>
            <div class="sigpac-input-wrap">
              <DxTextBox
                class="sigpac-input"
                :value="code"
                :read-only="true"
              />
              <DxButton
                class="sigpac-remove"
                icon="trash"
                type="danger"
                styling-mode="outlined"
                @click="removeDefaultSigpac(idx)"
              />
            </div>
          </div>
          <div
            v-for="(code, idx) in sigpacInputs"
            :key="`sigpac-extra-${idx}`"
            class="sigpac-row"
          >
            <div class="sigpac-label">
              {{ `${$t('Utilities.pao.sigpac')} ${plantationSigpac.length + idx + 1}` }}
            </div>
            <div class="sigpac-input-wrap">
              <DxTextBox
                class="sigpac-input"
                :value="code"
                :placeholder="sigpacPlaceholder"
                :show-clear-button="true"
                :max-length="sigpacMaxLength"
                value-change-event="input"
                @value-changed="(e) => onSigpacChange(idx, e.value)"
              >
                <DxValidator>
                  <DxPatternRule
                    :pattern="sigpacDisplayPattern"
                    :message="$t('Utilities.pao.sigpacInvalid')"
                  />
                </DxValidator>
              </DxTextBox>
              <DxButton
                class="sigpac-remove"
                icon="trash"
                type="danger"
                styling-mode="outlined"
                @click="removeSigpac(idx)"
              />
            </div>
          </div>
          <div class="sigpac-add-cell">
            <DxButton
              class="sigpac-add"
              icon="add"
              type="default"
              styling-mode="contained"
              @click="addSigpac"
            />
          </div>
        </div>
      </div>

      <div class="content-devx">
        <div class="card-header">
          {{ $t('Utilities.pao.productApplicationTitle') }}
        </div>
        <DxDataGrid
          ref="paoGrid"
          class="pao-product-grid"
          :data-source="productRows"
          :show-borders="true"
          :row-alternation-enabled="true"
          :word-wrap-enabled="true"
          key-expr="id"
          @row-updated="onRowUpdated"
          @cell-prepared="onCellPrepared"
          @editor-preparing="onEditorPreparing"
        >
          <DxEditing :allow-updating="true" mode="cell" />
          <DxColumn
            data-field="name"
            :caption="$t('Utilities.pao.productColumn')"
            :allow-editing="false"
            :allow-sorting="false"
            :width="250"
          />
          <DxColumn
            data-field="fertilizing_material_code"
            :caption="$t('Utilities.pao.fertilizingMaterialColumn')"
            :allow-sorting="false"
            :editor-options="{ wrapItemText: true }"
          >
            <DxLookup
              :data-source="fertilizingMaterialCatalog"
              value-expr="code"
              display-expr="name"
            />
          </DxColumn>
          <DxColumn
            data-field="application_method_code"
            :caption="$t('Utilities.pao.applicationMethodColumn')"
            :allow-sorting="false"
            :editor-options="{ wrapItemText: true }"
          >
            <DxLookup
              :data-source="applicationMethodCatalog"
              value-expr="code"
              display-expr="name"
            />
          </DxColumn>
          <DxColumn
            data-field="fertilization_type_code"
            :caption="$t('Utilities.pao.fertilizationTypeColumn')"
            :allow-sorting="false"
            :editor-options="{ wrapItemText: true }"
          >
            <DxLookup
              :data-source="fertilizationTypeCatalog"
              value-expr="code"
              display-expr="name"
            />
          </DxColumn>
          <DxColumn
            data-field="distribution_machinery"
            :caption="$t('Utilities.pao.machineryColumn')"
            :allow-sorting="false"
          />
        </DxDataGrid>
      </div>

      <stepper-navigation :disabled="false" @save="save" />

      <DxLoadPanel
        v-model:visible="loadingVisible"
        :position="position"
        shading-color="rgba(0,0,0,0.4)"
      />
    </section>
  </f7-page>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxLoadIndicator } from 'devextreme-vue/load-indicator';
import { DxDataGrid, DxColumn, DxEditing, DxLookup } from 'devextreme-vue/data-grid';
import DxTextBox from 'devextreme-vue/text-box';
import DxButton from 'devextreme-vue/button';
import { DxPopover } from 'devextreme-vue/popover';
import { DxValidator, DxPatternRule } from 'devextreme-vue/validator';
import navbar from '../../../components/NavBar.vue';
import Stepper from '../../../components/wizardNutrition/stepper/index.vue';
import StepperNavigation from '../../../components/wizardNutrition/stepper-navigation/index.vue';
import PopupConfirmWizardExit from '../../../components/wizardNutrition/popupConfirmWizardExit/index.vue';
import { fertilizationService } from '../../../services/fertilizationService';
import { fetchPlantationSigpacCodes } from '../../../services/plantationSigpacService';

export default {
  name: 'NutritionStepPao',
  components: {
    navbar,
    Stepper,
    StepperNavigation,
    PopupConfirmWizardExit,
    DxLoadPanel,
    DxLoadIndicator,
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxLookup,
    DxTextBox,
    DxButton,
    DxPopover,
    DxValidator,
    DxPatternRule,
  },
  data() {
    return {
      loadingVisible: false,
      position: { of: '#nutrition-step-pao' },
      // Provincia-Municipio-Agregado-Zona-Poligono-Parcela-Recinto (7 grupos, todos obligatorios).
      // Sin espacios: mismo formato que el SIGPAC auto-cargado desde GIS (composeSigpacReference).
      sigpacPlaceholder: 'XX-XXX-XXX-XXX-XXX-XXXXX-XXXX',
      sigpacSegments: [2, 3, 3, 3, 3, 5, 4],
      // Solo Provincia tiene longitud realmente fija → auto-insert de guion; el resto son
      // "hasta" (variable, ver ejemplos reales tipo 18-161-0-0-38-9015-1) y el usuario
      // inserta el guion para terminar el bloque.
      sigpacAutoBreak: [true, false, false, false, false, false, false],
      sigpacPattern: /^\d{1,2}-\d{1,3}-\d{1,3}-\d{1,3}-\d{1,3}-\d{1,5}-\d{1,4}$/,
      sigpacDisplayPattern: /^\d{1,2}-\d{1,3}-\d{1,3}-\d{1,3}-\d{1,3}-\d{1,5}-\d{1,4}$/,
      sigpacInputs: [],
      plantationSigpac: [],
      excludedSigpac: [],
      sigpacLoading: false,
      sigpacTooltipVisible: false,
      productRows: [],
    };
  },
  computed: {
    // total digits + "-" separators between blocks
    sigpacMaxLength() {
      const digits = this.sigpacSegments.reduce((a, b) => a + b, 0);
      return digits + (this.sigpacSegments.length - 1);
    },
    ...mapGetters('nutritionStepper', ['stepData']),
    ...mapGetters('Settings', ['isGisV2']),
    ...mapState('Plantation', ['currentPlantation']),
    ...mapState('Product', ['fertilizingMaterialCatalog', 'applicationMethodCatalog', 'fertilizationTypeCatalog']),
  },
  async mounted() {
    this.sigpacLoading = true;
    const existing = this.stepData.sigpac_codes ?? [];
    this.sigpacInputs = existing.length > 0 ? existing.map((c) => this.formatSigpac(c)) : [];
    this.excludedSigpac = (this.stepData.excluded_sigpac_codes ?? []).map((c) => this.normalizeSigpac(c));
    this.productRows = (this.stepData.dosageProduct ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      fertilizing_material_code: p.fertilizing_material_code ?? p.fertilizing_material?.code ?? null,
      fertilizing_material_name: p.fertilizing_material_name ?? p.fertilizing_material?.name ?? null,
      application_method_code: p.application_method_code ?? p.application_method?.code ?? null,
      application_method_name: p.application_method_name ?? p.application_method?.name ?? null,
      fertilization_type_code: p.fertilization_type_code ?? p.fertilization_type?.code ?? null,
      fertilization_type_name: p.fertilization_type_name ?? p.fertilization_type?.name ?? null,
      distribution_machinery: p.distribution_machinery ?? null,
      isTimac: [1, 2].includes(Number(p.tipology)),
    }));
    if (!this.fertilizingMaterialCatalog?.length) await this.getFertilizingMaterialCatalog();
    if (!this.applicationMethodCatalog?.length) await this.getApplicationMethodCatalog();
    if (!this.fertilizationTypeCatalog?.length) await this.getFertilizationTypeCatalog();
    try {
      const plantationCodes = await fetchPlantationSigpacCodes(
        this.currentPlantation?.companyId,
        this.currentPlantation?.sectorId,
        this.isGisV2,
      );
      this.plantationSigpac = plantationCodes.filter(
        (code) => !this.excludedSigpac.includes(this.normalizeSigpac(code)),
      );
    } finally {
      this.sigpacLoading = false;
    }
  },
  beforeDestroy() {
    this.commitSigpacCodes();
  },
  methods: {
    ...mapActions('Product', ['getFertilizingMaterialCatalog', 'getApplicationMethodCatalog', 'getFertilizationTypeCatalog']),
    commitSigpacCodes() {
      this.stepData.sigpac_codes = this.sigpacInputs
        .map((v) => this.normalizeSigpac(v))
        .filter((v) => v.length > 0);
      this.stepData.excluded_sigpac_codes = [...new Set(this.excludedSigpac)];
    },
    formatSigpac(raw) {
      const maxes = this.sigpacSegments;
      const chars = (raw ?? '').replace(/[^\d-]/g, '');
      const segs = [''];
      for (const ch of chars) {
        let i = segs.length - 1;
        if (ch === '-') {
          // user ends current block early (variable-length fields)
          if (segs[i] !== '' && segs.length < maxes.length) segs.push('');
          continue;
        }
        if (segs[i].length >= maxes[i]) {
          if (segs.length >= maxes.length) continue; // full code, drop extra digits
          segs.push('');
          i += 1;
        }
        segs[i] += ch;
      }
      let out = segs.join('-');
      const last = segs.length - 1;
      // only auto-append the dash after a filled fixed-length block (Provincia)
      if (
        segs.length < maxes.length
        && this.sigpacAutoBreak[last]
        && segs[last].length === maxes[last]
      ) {
        out += '-';
      }
      return out;
    },
    // Strips spaces/trailing dash and drops leading zeros per group (0 stays 0), so the same
    // parcela is always saved the same way regardless of how the user padded it — matching the
    // format the plantation's auto-loaded SIGPAC already uses (composeSigpacReference).
    normalizeSigpac(value) {
      return (value ?? '')
        .replace(/\s+/g, '')
        .replace(/-+$/, '')
        .split('-')
        .map((seg) => seg.replace(/^0+(?=\d)/, ''))
        .join('-');
    },
    onSigpacChange(idx, value) {
      this.$set(this.sigpacInputs, idx, this.formatSigpac(value));
    },
    addSigpac() {
      this.sigpacInputs.push('');
    },
    removeDefaultSigpac(idx) {
      const [removed] = this.plantationSigpac.splice(idx, 1);
      this.excludedSigpac.push(this.normalizeSigpac(removed));
    },
    findProduct(id) {
      return this.stepData.dosageProduct.find((p) => p.id === id);
    },
    syncRowToDosage(row) {
      const p = this.findProduct(row.id);
      if (!p) return;
      p.fertilizing_material_code = row.fertilizing_material_code ?? null;
      p.fertilizing_material_name = row.fertilizing_material_name ?? null;
      p.application_method_code = row.application_method_code ?? null;
      p.application_method_name = row.application_method_name ?? null;
      p.fertilization_type_code = row.fertilization_type_code ?? null;
      p.fertilization_type_name = row.fertilization_type_name ?? null;
      p.distribution_machinery = row.distribution_machinery ?? null;
    },
    isLockedCell(dataField, row) {
      return dataField === 'fertilizing_material_code' && row?.isTimac === true;
    },
    onCellPrepared(e) {
      if (e.rowType !== 'data') return;
      if (e.column.dataField === 'name') {
        e.cellElement.style.color = '#333333';
        e.cellElement.style.backgroundColor = '#D9D9D9';
        e.cellElement.style.fontWeight = '500';
        e.cellElement.style.pointerEvents = 'none';
      }
      if (this.isLockedCell(e.column.dataField, e.data)) {
        e.cellElement.classList.add('pao-cell-readonly');
      }
    },
    onEditorPreparing(e) {
      if (e.parentType !== 'dataRow') return;
      if (this.isLockedCell(e.dataField, e.row?.data)) e.cancel = true;
    },
    onRowUpdated(e) {
      const row = e.data;
      const mat = this.fertilizingMaterialCatalog.find((c) => c.code === row.fertilizing_material_code) || null;
      row.fertilizing_material_name = mat ? mat.name : null;
      const met = this.applicationMethodCatalog.find((c) => c.code === row.application_method_code) || null;
      row.application_method_name = met ? met.name : null;
      const typ = this.fertilizationTypeCatalog.find((c) => c.code === row.fertilization_type_code) || null;
      row.fertilization_type_name = typ ? typ.name : null;
      this.syncRowToDosage(row);
    },
    async save() {
      this.$refs.paoGrid.instance.saveEditData();
      const trimmed = this.sigpacInputs
        .map((v) => this.normalizeSigpac(v))
        .filter((v) => v.length > 0);
      const hasInvalid = trimmed.some((v) => !this.sigpacPattern.test(v));
      if (hasInvalid) {
        this.$notifyDX(
          {
            message: this.$t('Utilities.pao.sigpacInvalid'),
            width: 550,
          },
          'error',
          3000,
        );
        return;
      }
      this.productRows.forEach((row) => {
        const p = this.findProduct(row.id);
        if (!p) return;
        p.fertilizing_material_code = row.fertilizing_material_code ?? null;
        p.fertilizing_material_name = row.fertilizing_material_name ?? null;
        p.application_method_code = row.application_method_code ?? null;
        p.application_method_name = row.application_method_name ?? null;
        p.fertilization_type_code = row.fertilization_type_code ?? null;
        p.fertilization_type_name = row.fertilization_type_name ?? null;
        p.distribution_machinery = row.distribution_machinery ?? null;
      });
      this.commitSigpacCodes();
      this.loadingVisible = true;
      this.setPendingPaoSave(true);
      this.back();
      this.$f7.views.main.router.navigate('/utilities/nutrition/nutrition-step-five', { reloadCurrent: true });
    },
    removeSigpac(idx) {
      this.sigpacInputs.splice(idx, 1);
    },
    ...mapActions('nutritionStepper', ['back', 'setPendingPaoSave']),
  },
};
</script>

<style lang="scss" scoped>
.pao-product-grid::v-deep .dx-header-row td[role='columnheader'] {
  text-transform: capitalize;
}
.pao-product-grid::v-deep td.pao-cell-readonly {
  color: #777;
  background-color: #F2F2F2;
  pointer-events: none;
}
.nutrition-step-pao {
  .sigpac-section {
    .sigpac-header {
      display: flex;
      align-items: center;
      gap: 6px;
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 12px;
    }
    .sigpac-title {
      letter-spacing: 0.02em;
    }
    .sigpac-info {
      font-size: 14px;
      color: #0071BD;
      cursor: help;
    }
    .sigpac-tooltip-body {
      padding: 10px;
      font-size: 12px;
      line-height: 1.4;
    }
    .sigpac-loading {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 0;
      font-size: 13px;
      color: #555;
    }
    .sigpac-rows {
      display: grid;
      // El placeholder/valor de 7 grupos (~41 caracteres) no cabe en una rejilla fija de 4
      // columnas; con un ancho mínimo por caja se ajustan menos columnas cuando hace falta.
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 12px 16px;
    }
    ::v-deep .sigpac-input.dx-texteditor {
      width: 100%;
      min-width: 0;
    }
    .sigpac-row {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
    .sigpac-label {
      font-size: 12px;
      font-weight: 500;
      color: #555;
      margin-bottom: 4px;
      text-transform: uppercase;
    }
    .sigpac-input-wrap {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .sigpac-input {
      flex: 1 1 auto;
      min-width: 0;
    }
    .sigpac-add,
    .sigpac-remove {
      flex: 0 0 auto;
    }
    .sigpac-add-cell {
      display: flex;
      align-items: flex-end;
      padding-bottom: 2px;
    }
  }
}
</style>

<style lang="scss">
.sigpac-tooltip-popover {
  .dx-overlay-content {
    background: #000 !important;
    border: none !important;
    border-radius: 8px !important;
    overflow: hidden;
  }
  .dx-popup-content {
    background: #000 !important;
    color: #fff !important;
    border: none !important;
    padding: 10px 12px !important;
  }
  .dx-popover-arrow::after {
    background: #000 !important;
    border-color: #000 !important;
  }
}
</style>
